import { forwardRef, useId } from 'react';

import type { TextAreaProps } from './types';
import { Label } from '../../general/Label';
import { cn } from '../../utils/cn';

export type { TextAreaProps } from './types';

/**
 * A multi-line form field that mirrors {@link TextInput} for the design system.
 *
 * @param id - ID of the textarea element. Used for htmlFor association.
 * @param label - Optional label rendered above the field.
 * @param error - Error message to display below the field.
 * @param disabled - Whether the field is disabled.
 * @param rows - Number of visible text rows. Defaults to 3.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param textareaClassName - Additional CSS classes for the textarea itself.
 * @param ...props - Additional HTML textarea attributes.
 *
 * @example
 * <TextArea id="note" label="Notiz" placeholder="Text" rows={4} />
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({ className, disabled, error, id, label, labelClassName, rows = 3, textareaClassName, ...props }, ref) => {
        const generatedId = useId();
        const fieldId = id ?? generatedId;
        const errorId = `${fieldId}-error`;
        const hasError = Boolean(error);

        return (
            <div className={cn('flex min-w-0 flex-col gap-2', className)}>
                {label && (
                    <Label htmlFor={fieldId} className={cn('w-full justify-start', labelClassName)}>
                        {label}
                    </Label>
                )}
                <textarea
                    ref={ref}
                    id={fieldId}
                    rows={rows}
                    aria-invalid={hasError || undefined}
                    aria-describedby={hasError ? errorId : undefined}
                    disabled={disabled}
                    className={cn(
                        'w-full min-w-0 rounded border bg-white px-3 py-2',
                        'text-sm text-midnight placeholder:text-midnight-placeholder',
                        'outline-none focus:ring-2 focus:ring-blue-focus focus:ring-offset-1',
                        hasError ? 'border-alert-error' : 'border-grey-dark',
                        disabled && 'cursor-not-allowed bg-surface-light opacity-50',
                        textareaClassName,
                    )}
                    {...props}
                />
                {hasError && (
                    <span id={errorId} className="mt-1 text-[10px] leading-[1.46] text-alert-error">
                        {error}
                    </span>
                )}
            </div>
        );
    },
);

TextArea.displayName = 'TextArea';
